/**
 * EFT Tapping flashcard exercises — data-driven.
 *
 * Every exercise uses the same flashcard flow: intro → intensity check →
 * setup (karate chop) → 8 tapping point cards → pause → re-check → completion.
 * To add a new exercise, add an entry here. No interface changes needed.
 *
 * Educational self-help only — not therapy, medical treatment or crisis support.
 */

import type { TappingPointId } from "@/lib/tapping-data";

/** The eight sequence points, always in this order. */
export const EFT_SEQUENCE: TappingPointId[] = [
  "eyebrow",
  "side-of-eye",
  "under-eye",
  "under-nose",
  "chin",
  "collarbone",
  "under-arm",
  "top-of-head",
];

export const EFT_SHORT_CODE: Record<TappingPointId, string> = {
  "karate-chop": "KC",
  eyebrow: "EB",
  "side-of-eye": "SE",
  "under-eye": "UE",
  "under-nose": "UN",
  chin: "CH",
  collarbone: "CB",
  "under-arm": "UA",
  "top-of-head": "TH",
};

export type EftExercise = {
  slug: string;
  emoji: string;
  title: string;
  /** When to use it — shown on the intro card and the listing. */
  description: string;
  /** Repeated 3× while tapping the karate chop point. */
  setup: string;
  /** One phrase per point, in EFT_SEQUENCE order (8 phrases). */
  phrases: Record<Exclude<TappingPointId, "karate-chop">, string>;
  /** Shown at the end of a round, guiding the re-check. */
  roundClose: string;
  /** Shown on the completion card. */
  completion: string;
};

export const EFT_EXERCISES: EftExercise[] = [
  {
    slug: "morning-dread",
    emoji: "🌅",
    title: "Morning Dread",
    description: "For when you wake up already anxious about the day.",
    setup:
      "Even though I woke up with this heavy dread in my chest and I don't know how I'll face the day, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This morning dread",
      "side-of-eye": "This heaviness in my chest",
      "under-eye": "I feel already behind",
      "under-nose": "So much pressure today",
      chin: "I just want to hide under the covers",
      collarbone: "This fear of the day ahead",
      "under-arm": "It feels overwhelming",
      "top-of-head": "Releasing this morning dread",
    },
    roundClose: "Repeat the round until the dread feels manageable.",
    completion: "You met the morning gently. However small the shift, it counts.",
  },
  {
    slug: "sunday-scaries",
    emoji: "📅",
    title: "The Sunday Scaries",
    description: "For the pit in your stomach before the work week.",
    setup:
      "Even though I have this pit in my stomach thinking about Monday and all the things I have to do, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This Sunday anxiety",
      "side-of-eye": "The pit in my stomach",
      "under-eye": "Dreading tomorrow",
      "under-nose": "So much to do already",
      chin: "I can't relax today",
      collarbone: "The pressure of the week",
      "under-arm": "It feels heavy",
      "top-of-head": "Releasing this Sunday dread",
    },
    roundClose: "Repeat the round until the anxiety feels manageable.",
    completion: "The week ahead can wait. Right now, you're here.",
  },
  {
    slug: "inbox-overwhelm",
    emoji: "📥",
    title: "Inbox Overwhelm",
    description: "For when the unread messages pile up and you feel paralysed.",
    setup:
      "Even though I look at my inbox and feel completely paralysed by the volume of emails, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This email overwhelm",
      "side-of-eye": "So many messages",
      "under-eye": "I feel paralysed",
      "under-nose": "I don't know where to start",
      chin: "It's too much",
      collarbone: "This pressure to reply",
      "under-arm": "I just want to close the tab",
      "top-of-head": "Releasing this inbox paralysis",
    },
    roundClose: "Repeat the round until the overwhelm feels manageable.",
    completion: "One message at a time is enough. Nothing has to be answered at once.",
  },
  {
    slug: "imposter-syndrome",
    emoji: "🎭",
    title: "Imposter Syndrome",
    description: "For right before a big meeting or presentation.",
    setup:
      "Even though I feel like a fraud and everyone is about to find out I don't belong here, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This feeling of being a fraud",
      "side-of-eye": "Everyone will know",
      "under-eye": "I don't belong here",
      "under-nose": "I'm faking it",
      chin: "This pressure to perform",
      collarbone: "This fear of being exposed",
      "under-arm": "I'm not good enough",
      "top-of-head": "Releasing this imposter fear",
    },
    roundClose: "Repeat the round until the self-doubt feels manageable.",
    completion: "You are allowed to take up the space you're already standing in.",
  },
  {
    slug: "post-zoom-fatigue",
    emoji: "💻",
    title: "Post-Zoom Fatigue",
    description: "Reconnecting to your body after hours on screen.",
    setup:
      "Even though my brain feels fried and I feel disconnected from my body after that video call, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This screen fatigue",
      "side-of-eye": "My brain feels fuzzy",
      "under-eye": "Feeling like a floating head",
      "under-nose": "So much talking",
      chin: "My energy is drained",
      collarbone: "Need to ground my body",
      "under-arm": "This digital exhaustion",
      "top-of-head": "Reconnecting to myself now",
    },
    roundClose: "Repeat the round until you feel present in your body again.",
    completion: "You're back in your body. Feel your feet, and take one slow breath.",
  },
  {
    slug: "financial-anxiety",
    emoji: "💷",
    title: "Financial Anxiety",
    description: "For when money worries make your body feel unsafe.",
    setup:
      "Even though I feel this tight knot of fear in my stomach when I think about money and my future feels uncertain, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This money fear",
      "side-of-eye": "The knot in my stomach",
      "under-eye": "I don't feel safe",
      "under-nose": "So much uncertainty",
      chin: "What if I can't manage?",
      collarbone: "This scarcity feeling",
      "under-arm": "The pressure is heavy",
      "top-of-head": "Releasing this financial stress",
    },
    roundClose: "Repeat the round until the fear feels manageable.",
    completion: "The worry may still be there, and your body can be a little calmer alongside it.",
  },
  {
    slug: "to-do-list-freeze",
    emoji: "🧊",
    title: "The To-Do List Freeze",
    description: "Breaking paralysis when there is far too much to do.",
    setup:
      "Even though I'm completely frozen and can't start anything because my to-do list is too long and overwhelming, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This freeze response",
      "side-of-eye": "So many tasks",
      "under-eye": "I'm paralysed",
      "under-nose": "Where do I even start?",
      chin: "It's all too much",
      collarbone: "This pressure to be productive",
      "under-arm": "My brain is locked up",
      "top-of-head": "Releasing this paralysis now",
    },
    roundClose: "Repeat the round until you feel able to take one small step.",
    completion: "One small step is a complete step. You choose which one.",
  },
  {
    slug: "boundary-guilt",
    emoji: "🚪",
    title: "Boundary Guilt",
    description: "For after you said no to someone and feel bad about it.",
    setup:
      "Even though I said no and now I feel a heavy pit of guilt in my stomach and worry I've disappointed them, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This guilt for saying no",
      "side-of-eye": "I feel like I was selfish",
      "under-eye": "Worried they are upset",
      "under-nose": "It's hard to disappoint people",
      chin: "This heavy feeling in my chest",
      collarbone: "Wanting to take it back",
      "under-arm": "I'm allowed to have boundaries",
      "top-of-head": "Releasing this boundary guilt",
    },
    roundClose: "Repeat the round until you feel more at peace with your decision.",
    completion: "Your no was allowed. Guilt can be present without meaning you were wrong.",
  },
  {
    slug: "anger-resentment",
    emoji: "🔥",
    title: "Anger & Resentment",
    description: "Releasing heat from the chest and jaw.",
    setup:
      "Even though I feel this burning anger in my chest and my jaw is clenched tight with resentment, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This burning anger",
      "side-of-eye": "So much resentment",
      "under-eye": "My jaw is clenched",
      "under-nose": "It feels hot in my chest",
      chin: "I can't let it go",
      collarbone: "This old anger",
      "under-arm": "Holding it in my body",
      "top-of-head": "Releasing this heat now",
    },
    roundClose: "Repeat the round until the heat in your body cools a little.",
    completion: "Anger made sense. It's safe to let your body set some of it down.",
  },
  {
    slug: "physical-pain-tension",
    emoji: "🫁",
    title: "Physical Pain & Tension",
    description: "Acknowledging the headache or aching body without judgment.",
    setup:
      "Even though I have this throbbing pain in my head and tension in my back that won't go away, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This throbbing pain",
      "side-of-eye": "My aching back",
      "under-eye": "This tension I'm holding",
      "under-nose": "It feels stuck",
      chin: "Acknowledging this pain",
      collarbone: "Without judgment",
      "under-arm": "It's safe to feel this",
      "top-of-head": "Releasing this physical tension",
    },
    roundClose: "Repeat the round until the intensity softens.",
    completion:
      "Tapping is not a substitute for medical care. If pain is new, severe or persistent, please speak with a doctor.",
  },
  {
    slug: "racing-thoughts-at-night",
    emoji: "🌙",
    title: "Racing Thoughts at Night",
    description: "Closing the mental tabs so sleep has room.",
    setup:
      "Even though my mind is racing with a million thoughts and I can't seem to turn off my brain to sleep, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This busy brain",
      "side-of-eye": "So many mental tabs open",
      "under-eye": "Can't switch off",
      "under-nose": "Replaying the day",
      chin: "Worrying about tomorrow",
      collarbone: "My mind won't stop",
      "under-arm": "Need to quiet the noise",
      "top-of-head": "Releasing these racing thoughts",
    },
    roundClose: "Repeat the round until your mind feels calm enough for rest.",
    completion: "Nothing more needs solving tonight. Let your breath slow down.",
  },
  {
    slug: "future-tripping",
    emoji: "🔭",
    title: "Future Tripping",
    description: "Bringing your mind back from 'what if' to right now.",
    setup:
      "Even though my mind is jumping way ahead to the future, worrying about things that haven't happened and may never happen, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This future tripping",
      "side-of-eye": "Worrying about 'what if'",
      "under-eye": "I'm not here right now",
      "under-nose": "Scared of the unknown",
      chin: "Trying to control it all",
      collarbone: "It's exhausting",
      "under-arm": "Come back to my feet",
      "top-of-head": "Releasing the future",
    },
    roundClose: "Repeat the round until you feel more grounded in the present.",
    completion: "This moment is the only one you have to be in. Feel your feet on the floor.",
  },
  {
    slug: "shame-spiral",
    emoji: "🕯️",
    title: "Shame Spiral",
    description: "Deep compassion for when you feel you got something wrong.",
    setup:
      "Even though I feel this deep sense of shame and just want to hide because I feel like I've done something wrong, I deeply and completely accept myself.",
    phrases: {
      eyebrow: "This heavy shame",
      "side-of-eye": "I want to disappear",
      "under-eye": "Feeling like a failure",
      "under-nose": "Everyone is judging me",
      chin: "This pit in my stomach",
      collarbone: "It feels too big",
      "under-arm": "Holding onto this mistake",
      "top-of-head": "Releasing this shame spiral",
    },
    roundClose: "Repeat the round until a little self-compassion returns.",
    completion: "Shame lies about who you are. You are allowed to be a person who is learning.",
  },
  {
    slug: "celebration-tap",
    emoji: "✨",
    title: "The Celebration Tap",
    description: "Locking in a win — because anxious people often skip celebrating.",
    setup:
      "Even though it feels unfamiliar to stop and celebrate this win, and part of me is already worrying about what's next, I choose to pause and deeply accept myself and this victory.",
    phrases: {
      eyebrow: "This win is mine",
      "side-of-eye": "I worked hard for this",
      "under-eye": "It's safe to feel good",
      "under-nose": "Pausing to acknowledge it",
      chin: "This feeling of accomplishment",
      collarbone: "Letting it sink in",
      "under-arm": "I am proud of myself",
      "top-of-head": "Locking in this victory",
    },
    roundClose: "Repeat the round until the pride and warmth feel real.",
    completion: "Let it land. You're allowed to feel good about this.",
  },
];

export function getEftExercise(slug: string): EftExercise | null {
  return EFT_EXERCISES.find((e) => e.slug === slug) ?? null;
}
